import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '../api'
import { ImportDialog } from '../ImportDialog'
import { Logo, Modal, Spinner, toast, undoToast } from '../ui'
import type { Beat, Comment, Select, Take, Transcript, Viewer } from '../types'
import { ArrowDownIcon, ArrowUpIcon, CheckIcon, CloseIcon, CommentIcon, DownloadIcon, HeartIcon, ChevronDownIcon, MagnifyingGlassIcon, MoreHorizontalIcon, PanelLeftCloseIcon, PanelLeftOpenIcon, PencilSimpleIcon, PlayIcon, PlusIcon, ShareIcon, TrashIcon, UsersIcon, LinkIcon, GlobeIcon, LockIcon } from '../icons'
import { clock, cls, copy, flatten, interviews, paragraphs, plural, speakerAt, speakerName, takeTc, wordAt, type FlatWord, type Interview } from '../util'
import { buildSteps, CamerasDialog, ProcessingChip, ProcessingPanel, useMediaStatus } from './Processing'
import { SplitDialog } from './Split'

/** Any interview menu or hint can ask to split an interview into people. */
const askSplit = (key: string) => window.dispatchEvent(new CustomEvent('osel:split', { detail: key }))
import { MultiCam, type CamApi } from './MultiCam'
import { filmicLook, lookCams, lookLabel, LOOKS, lutFor } from './look'
import { createPlayhead, useProject, type PlayheadStore } from './store'

/*
  The review screen. Clients do one thing here: read the interviews next to
  the footage and mark what they love (a favorite, optionally a comment and a
  chapter tag). Editors use the same screen to see everyone's picks.
*/
export function ProjectView({ projectId }: { projectId: string; anthropic?: boolean }) {
  const { data, transcripts, error, media, takes, actions } = useProject(projectId)
  const playhead = useMemo(createPlayhead, [])
  const cam = useRef<CamApi | null>(null)
  const [ivKey, setIvKey] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [flash, setFlash] = useState<{ take: string; t: number; n: number } | null>(null)
  const [sharing, setSharing] = useState(false)
  const [cutting, setCutting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [procOpen, setProcOpen] = useState(false)
  const [camsOpen, setCamsOpen] = useState(false)
  const [splitKey, setSplitKey] = useState<string | null>(null)
  useEffect(() => {
    const on = (e: Event) => setSplitKey((e as CustomEvent<string>).detail)
    window.addEventListener('osel:split', on)
    return () => window.removeEventListener('osel:split', on)
  }, [])
  const [mobileTab, setMobileTab] = useState<'read' | 'picks'>('read')
  const [who, setWho] = useState<'all' | 'mine'>('all')
  const [rail, setRail] = useState(() => {
    try { return localStorage.getItem('osel-rail') !== 'closed' } catch { return true }
  })
  const toggleRail = () => setRail((r) => {
    try { localStorage.setItem('osel-rail', r ? 'closed' : 'open') } catch {}
    return !r
  })

  const isTeam = data?.viewer.kind === 'team'
  const all = useMemo(() => (data ? interviews(data.takes, transcripts) : []), [data, transcripts])
  const shown = all.filter((iv) => !iv.hidden)
  const iv = shown.find((x) => x.key === ivKey) || shown[0]
  const chapters = data?.beats || []
  const picks = (data?.selects || []).filter((s) => !s.suggested)
  const mediaStatus = useMediaStatus(projectId, isTeam, data?.media.length || 0)
  const steps = useMemo(() => (data && isTeam ? buildSteps(data, mediaStatus) : []), [data, isTeam, mediaStatus])

  useEffect(() => {
    if (iv && iv.key !== ivKey) setIvKey(iv.key)
  }, [iv, ivKey])

  // The Osmo look. Each clip's camera profile is read from its metadata on the cloud, a few clips per
  // call, the first time the team opens the project after footage arrives.
  const unread = (data?.media || []).filter((m) => m.kind === 'video' && m.profile_at == null).length
  useEffect(() => {
    if (!isTeam || !unread) return
    let live = true
    ;(async () => {
      try {
        for (let i = 0; i < 30 && live; i++) {
          const r = await api.post<{ left: number; done: unknown[] }>(`p/${projectId}/detect`)
          if (!r.left || !r.done.length) break
        }
      } catch { /* tried again next open */ }
      if (live) actions.reload()
    })()
    return () => {
      live = false
    }
  }, [isTeam, projectId, unread > 0]) // eslint-disable-line react-hooks/exhaustive-deps
  const project = data?.project
  const lutOf = useCallback((label: string, mid: string | undefined) => lutFor(project, mid ? media.get(mid) : undefined, label), [project, media])
  // The look menu sits in the player controls. Clients see it once any camera is log (by its metadata
  // or the team's say) and the team lets them pick; the team always sees it, and picking a look before
  // any camera is set opens Cameras & sound to say what the footage is.
  const anyLog = !!data && (Object.values(lookCams(data.project)).some((k) => k !== 'none') || data.media.some((m) => m.profile && m.profile !== 'none'))
  const canPick = isTeam || !!data?.project.look_client
  const look = data && (anyLog || isTeam)
    ? {
        value: data.project.look || '',
        label: lookLabel(data.project.look) || null,
        // Filmic is kept as the project's setting but only draws on a film look (Rec.709 has no film).
        film: !!data.project.look_film && filmicLook(data.project.look),
        filmic: !data.project.look || filmicLook(data.project.look),
        onFilm: canPick
          ? (on: boolean) => actions.setLook({ film: on }).catch((e) => toast(e instanceof ApiError ? e.message : 'Could not change the look', 'err'))
          : undefined,
        options: LOOKS,
        onChange: canPick
          ? (key: string) => {
              actions.setLook({ look: key }).catch((e) => toast(e instanceof ApiError ? e.message : 'Could not change the look', 'err'))
              if (key && !anyLog) {
                toast('Set each camera profile so the look knows how to read the footage')
                setCamsOpen(true)
              }
            }
          : undefined,
      }
    : undefined

  // Opening an interview parks the player on the first frame where every
  // camera is rolling, so the first thing on screen is picture and space plays.
  const ivFirstTake = iv?.takes[0]?.id
  useEffect(() => {
    if (!iv || !data) return
    const cur = cam.current?.current()
    if (cur?.takeId && iv.takes.some((t) => t.id === cur.takeId)) return
    const take = iv.takes[0]
    cam.current?.cue(take.id, rollStart(take, media))
  }, [ivFirstTake, !!data]) // eslint-disable-line react-hooks/exhaustive-deps

  // Space plays from wherever the playhead was parked; J/K/L and arrows nudge.
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement
      if (el?.closest('input, textarea, select, [contenteditable="true"]') || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === ' ') {
        e.preventDefault()
        const cur = cam.current?.current()
        if (!cur?.takeId && iv) cam.current?.play(iv.takes[0].id, 0)
        else cam.current?.toggle()
      } else if (e.key === 'k' || e.key === 'K') cam.current?.pause()
      else if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowLeft') cam.current?.nudge(-5)
      else if (e.key === 'l' || e.key === 'L' || e.key === 'ArrowRight') cam.current?.nudge(5)
      else if (e.key === '/') {
        e.preventDefault()
        document.getElementById('osel-search')?.focus()
      }
    }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [iv])

  const hits = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q.length < 2 || !data) return []
    const norm = (w: string) => w.toLowerCase().replace(/[^\p{L}\p{N}']/gu, '')
    const terms = q.split(/\s+/).map(norm).filter(Boolean)
    const out: { iv: Interview; take: Take; t: number; before: string; match: string; after: string; speaker: string }[] = []
    for (const group of shown)
      for (const take of group.takes)
        for (const seg of transcripts[take.id]?.segments || []) {
          const ws = seg.words.map((w) => w[0])
          const lw = ws.map(norm)
          for (let i = 0; i <= lw.length - terms.length; i++)
            if (terms.every((t, k) => lw[i + k]?.startsWith(t))) {
              out.push({ iv: group, take, t: seg.words[i][1], before: ws.slice(Math.max(0, i - 6), i).join(' '), match: ws.slice(i, i + terms.length).join(' '), after: ws.slice(i + terms.length, i + terms.length + 8).join(' '), speaker: speakerName(take, seg.speaker) })
              if (out.length > 300) return out
            }
        }
    return out
  }, [query, data, transcripts, all, shown, isTeam])

  if (error) {
    return (
      <div className="fullpage">
        <div className="signin">
          <Logo size={40} />
          <h1>{error.status === 404 ? 'Project not found' : error.status === 401 ? 'No access' : 'Something went wrong'}</h1>
          <p>{error.message}</p>
          <a className="btn" href="/">Back</a>
        </div>
      </div>
    )
  }
  if (!data) return <div className="fullpage"><Spinner /></div>

  const openJobs = data.jobs.filter((j) => j.status === 'queued' || j.status === 'running')
  const jump = (takeId: string, t: number) => {
    const g = all.find((x) => x.takes.some((tk) => tk.id === takeId))
    if (g) setIvKey(g.key)
    setFlash({ take: takeId, t, n: Date.now() })
    cam.current?.cue(takeId, t)
    setMobileTab('read')
  }
  const playLine = (p: Select) => {
    jump(p.take_id, p.start)
    cam.current?.playRange(p.take_id, p.start, p.end)
  }

  return (
    <div className={cls('app review', `m-${mobileTab}`)}>
      <header className="topbar">
        {isTeam ? (
          <a className="brand" href={`${REVIEWS}/`} title="All projects"><Logo /></a>
        ) : (
          <span className="brand"><Logo /></span>
        )}
        {data.takes.length > 0 && <button className="icon-btn rail-toggle" onClick={toggleRail} aria-label={rail ? 'Hide chapters' : 'Show chapters'} title={rail ? 'Hide chapters' : 'Show chapters'}>{rail ? <PanelLeftCloseIcon /> : <PanelLeftOpenIcon />}</button>}
        <div className="crumb">
          <span className="muted">{data.project.client}</span>
          {isTeam ? <a className="crumb-title" href={`${REVIEWS}/projects/${projectId}`}><b>{data.project.title}</b></a> : <b>{data.project.title}</b>}
        </div>
        {(isTeam || (data.viewer.kind === 'client' && data.viewer.billing)) && (
          <nav className="ptabs" aria-label="This project">
            {isTeam && <a href={`${REVIEWS}/projects/${projectId}`} title="Versions, reviews, the agent">edits</a>}
            <a className="on" aria-current="page">transcripts</a>
            <a href={`/billing/?p=${projectId}`} title="What this project has used, and what it costs">billing</a>
          </nav>
        )}
        <div className="grow" />
        {data.takes.length > 0 && (
          <div className="search top-search"><MagnifyingGlassIcon className="search-ico" />
            <input id="osel-search" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Escape' && setQuery('')} placeholder="Search every interview  /" aria-label="Search transcripts" />
            {query && <button className="icon-btn" onClick={() => setQuery('')} aria-label="Clear"><CloseIcon /></button>}
            {query.trim().length >= 2 && (
              <div className="hits hits-pop">
                <div className="muted small pad-x">{hits.length > 300 ? '300+' : hits.length} matches</div>
                {hits.map((h, i) => (
                  <button key={i} className="hit" onClick={() => jump(h.take.id, h.t)}>
                    <div className="hit-meta"><b>{h.speaker && h.speaker !== '?' ? h.speaker : h.iv.name}</b><span>{takeTc(h.take, h.t)}</span></div>
                    <div className="hit-text">…{h.before} <mark>{h.match}</mark> {h.after}…</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {isTeam && <ProcessingChip steps={steps} noMachine={openJobs.length > 0 && !data.workers.length} onOpen={() => setProcOpen(true)} />}
        {isTeam && data.takes.length > 0 && <button className="btn btn-ghost hide-sm" onClick={() => setCamsOpen(true)} title="Which file is which camera, and how they were lined up">Cameras & sound</button>}
        {isTeam && <button className="btn btn-icon" onClick={() => setImporting(true)} title="Add footage" aria-label="Add footage"><PlusIcon size={16} /></button>}
        {isTeam && picks.length > 0 && <button className="btn hide-sm" onClick={() => setCutting(true)} title="The Osmo agent cuts a V1 from everyone's picks; it lands on post.osmo.inc">First cut</button>}
        {isTeam && <button className="btn btn-primary" onClick={() => setSharing(true)}><ShareIcon /> Share</button>}
        {!isTeam && <span className="muted small viewer-name">{data.viewer.name}</span>}
      </header>

      {data.takes.length === 0 ? (
        <div className="empty big">
          {openJobs.length ? (
            <>
              <Spinner />
              <h3>{steps.find((x) => x.state === 'active')?.title || 'Getting started'}</h3>
              <p>{steps.find((x) => x.state === 'active')?.detail || 'Waiting for a machine to pick up the files.'}{steps.find((x) => x.state === 'active')?.eta ? ` (${steps.find((x) => x.state === 'active')!.eta})` : ''}</p>
              {isTeam && <button className="btn btn-ghost" onClick={() => setProcOpen(true)}>See every step</button>}
            </>
          ) : isTeam ? (
            <><h3>Add the shoot's footage</h3><p>Upload from your computer or pick files already on Osmo Cloud. Every camera plus sound: we line them up and transcribe.</p><button className="btn btn-primary" onClick={() => setImporting(true)}>Add footage</button></>
          ) : (<><h3>Nothing here yet</h3><p>Osmo is still preparing this project.</p></>)}
        </div>
      ) : (
        <div className={cls('layout3', !rail && 'rail-closed')}>
          <aside className="left">
            <div className="left-scroll">
              <FavoritesList picks={picks.filter((p) => !p.chapters.length && (who === 'all' || p.author === data.viewer.name))} who={who} setWho={setWho} people={new Set(picks.map((p) => p.author)).size} takes={takes} transcripts={transcripts} all={all} onJump={jump} actions={actions} viewer={data.viewer} onPlayLine={playLine} />
              <ChaptersPanel picks={picks.filter((p) => who === 'all' || p.author === data.viewer.name)} chapters={chapters} takes={takes} transcripts={transcripts} all={all} onJump={jump} actions={actions} viewer={data.viewer} onPlayLine={playLine} onPlayList={(list) => cam.current?.playQueue(list.map((p) => ({ takeId: p.take_id, start: p.start, end: p.end })))} />
            </div>
            <StringoutBar projectId={projectId} isTeam={isTeam} picks={picks.length} />
          </aside>

          <main className="center">
            {iv && (
              <InterviewTranscript
                key={iv.key}
                iv={iv}
                allIvs={shown}
                onPickIv={setIvKey}
                transcripts={transcripts}
                picks={picks}
                chapters={chapters}
                comments={data.comments}
                viewer={data.viewer}
                playhead={playhead}
                cam={cam}
                flash={flash}
                projectId={projectId}
                actions={actions}
              />
            )}
          </main>

          <aside className="right">
            <div className="right-player">
              <MultiCam projectId={projectId} takes={takes} media={media} playhead={playhead} apiRef={cam} sequence={iv?.takes} lutOf={lutOf} look={look} canDownload={data.viewer.kind === 'team' || data.viewer.canDownload} />
            </div>
          </aside>
        </div>
      )}

      <nav className="mobile-tabs">
        <button className={cls(mobileTab === 'read' && 'on')} onClick={() => setMobileTab('read')}>Transcript</button>
        <button className={cls(mobileTab === 'picks' && 'on')} onClick={() => setMobileTab('picks')}>Chapters <span className="count">{picks.length}</span></button>
      </nav>

      {sharing && <ShareDialog projectId={projectId} title={data.project.title} onClose={() => setSharing(false)} />}
      {cutting && <FirstCutDialog projectId={projectId} title={data.project.title} picks={picks} onClose={() => setCutting(false)} />}
      {importing && <ImportDialog projectId={projectId} onClose={() => setImporting(false)} onDone={() => { actions.reload(); setProcOpen(true) }} />}
      {procOpen && <ProcessingPanel steps={steps} noMachine={openJobs.length > 0 && !data.workers.length} onClose={() => setProcOpen(false)} onCameras={() => { setProcOpen(false); setCamsOpen(true) }} />}
      {splitKey && all.find((x) => x.key === splitKey) && <SplitDialog projectId={projectId} iv={all.find((x) => x.key === splitKey)!} transcripts={transcripts} onClose={() => setSplitKey(null)} onDone={() => actions.reload()} />}
      {camsOpen && <CamerasDialog projectId={projectId} data={data} media={media} onClose={() => setCamsOpen(false)} onChanged={() => actions.reload()} onAdd={() => { setCamsOpen(false); setImporting(true) }} setLook={actions.setLook} />}
    </div>
  )
}

type Actions = ReturnType<typeof useProject>['actions']

/** Take time where every camera is rolling (else where the first one starts). */
function rollStart(take: Take, media: Map<string, { duration: number | null }>): number {
  const labels = [...new Set(take.angles.map((a) => a.label))]
  if (!labels.length) return 0
  const firsts = labels.map((l) => Math.min(...take.angles.filter((a) => a.label === l).map((a) => a.offset)))
  const all = Math.max(...firsts)
  const anyEnd = Math.max(...take.angles.map((a) => a.offset + (media.get(a.media_id)?.duration ?? 0)))
  const t = all < anyEnd ? all : Math.min(...firsts)
  return Math.max(0, t + 0.05)
}

/** Keep a cue inside rolling picture: before the cameras start, snap to the first rolling frame. */
export function clampToRolling(take: Take, t: number): number {
  const start = Math.max(0, Math.min(...take.angles.map((a) => a.offset)) + 0.05)
  return t < start ? start : t
}

function InterviewRow({ iv, on, picks, isTeam, onClick, actions }: { iv: Interview; on: boolean; picks: number; isTeam?: boolean; onClick: () => void; actions: Actions }) {
  const [mode, setMode] = useState<'' | 'menu' | 'rename'>('')
  const [name, setName] = useState(iv.name)
  const busy = iv.takes.some((t) => t.status === 'queued' || t.status === 'processing')
  const cams = Math.max(0, ...iv.takes.map((t) => new Set(t.angles.map((a) => a.label)).size))
  const ids = iv.takes.map((t) => t.id)
  const save = () => {
    if (name.trim() && name.trim() !== iv.name) actions.renameInterview(ids, name.trim())
    setMode('')
  }
  return (
    <div className={cls('iv-row', on && 'on', mode && 'open')}>
      <div className="iv-line">
        {mode === 'rename' ? (
          <form className="iv-rename" onSubmit={(e) => { e.preventDefault(); save() }}>
            <span className="iv-check" />
            <input value={name} onChange={(e) => setName(e.target.value)} onBlur={save} onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setName(iv.name); setMode('') } }} aria-label="Interview name" maxLength={120} autoFocus onFocus={(e) => e.target.select()} />
          </form>
        ) : (
          <button className="iv-row-main" role="menuitemradio" aria-checked={on} onClick={onClick}>
            <span className="iv-check">{on && <CheckIcon />}</span>
            <span className="iv-name">{iv.name}</span>
            <span className="iv-dur">{clock(iv.duration)}</span>
            <span className="iv-meta">
              {busy ? <><Spinner small /> transcribing</> : <>{plural(iv.takes.length, 'take')}{cams > 1 ? ` · ${cams} cameras` : ''}</>}
              {picks > 0 && <span className="iv-favs"><HeartIcon className="i-heart" /> {picks}</span>}
            </span>
          </button>
        )}
        {isTeam && mode !== 'rename' && <button className="icon-btn iv-more" onClick={() => setMode(mode ? '' : 'menu')} aria-label="Interview options" aria-expanded={mode === 'menu'}><MoreHorizontalIcon /></button>}
      </div>
      {mode === 'menu' && (
        <div className="iv-acts">
          <button className="btn btn-sm" onClick={() => { setName(iv.name); setMode('rename') }}><PencilSimpleIcon size={14} /> Rename</button>
          <button className="btn btn-sm" onClick={() => { setMode(''); askSplit(iv.key) }}>Split into people</button>
          <button className="btn btn-sm" onClick={() => { setMode(''); actions.setHidden(ids, true); undoToast(`Removed "${iv.name}"`, () => actions.setHidden(ids, false)) }}><TrashIcon size={14} /> Remove from project</button>
        </div>
      )}
    </div>
  )
}

// The interview dropdown in the transcript header, which never scrolls away.
function InterviewPicker({ ivs, iv, picks, isTeam, onPick, actions }: { ivs: Interview[]; iv: Interview; picks: Select[]; isTeam: boolean; onPick: (key: string) => void; actions: Actions }) {
  const [open, setOpen] = useState(false)
  const menu = useRef<HTMLDivElement>(null)
  const btn = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!open) return
    menu.current?.querySelector<HTMLElement>('.iv-row.on .iv-row-main')?.focus()
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        btn.current?.focus()
      } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !(document.activeElement instanceof HTMLInputElement)) {
        const rows = [...(menu.current?.querySelectorAll<HTMLElement>('.iv-row-main') || [])]
        const i = rows.indexOf(document.activeElement as HTMLElement)
        rows[(i + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length]?.focus()
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [open])
  return (
    <div className="iv-picker">
      <button ref={btn} className="iv-picker-btn" onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open} aria-label="Choose interview">
        <h2>{iv.name}</h2>
        {/* With several interviews, say so on the button: a bare chevron read as decoration. */}
        {ivs.length > 1 ? (
          <span className="iv-switch">
            {ivs.findIndex((g) => g.key === iv.key) + 1} of {ivs.length} interviews
            <ChevronDownIcon />
          </span>
        ) : (
          <ChevronDownIcon />
        )}
      </button>
      {open && (
        <>
          <div className="iv-back" onMouseDown={() => setOpen(false)} />
          <div className="iv-menu" role="menu" aria-label="Interviews" ref={menu}>
            {ivs.map((g) => <InterviewRow key={g.key} iv={g} on={g.key === iv.key} picks={picks.filter((p) => g.takes.some((t) => t.id === p.take_id)).length} isTeam={isTeam} onClick={() => { onPick(g.key); setOpen(false) }} actions={actions} />)}
          </div>
        </>
      )}
    </div>
  )
}

const DASH = /^[-–—]+$/
const lineLen = (sec: number) => (sec < 60 ? `${Math.max(1, Math.round(sec))}s` : clock(sec))
const initials = (name: string) => name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?'

// ── transcript: every take of the interview, one scroll ───────────────────
type Pop = { kind: 'new'; take: Take; start: number; end: number; text: string; rect: DOMRect } | { kind: 'edit'; pick: Select; rect: DOMRect }

function InterviewTranscript({
  iv, allIvs, onPickIv, transcripts, picks, chapters, comments, viewer, playhead, cam, flash, projectId, actions,
}: {
  iv: Interview; allIvs: Interview[]; onPickIv: (key: string) => void; transcripts: Record<string, Transcript>; picks: Select[]; chapters: Beat[]; comments: Comment[]; viewer: Viewer
  playhead: PlayheadStore; cam: React.MutableRefObject<CamApi | null>; flash: { take: string; t: number; n: number } | null; projectId: string; actions: Actions
}) {
  const box = useRef<HTMLDivElement>(null)
  const [pop, setPop] = useState<Pop | null>(null)
  const isTeam = viewer.kind === 'team'
  const words = useMemo(() => Object.fromEntries(iv.takes.map((t) => [t.id, flatten(transcripts[t.id])])), [iv, transcripts])

  // Karaoke + follow (pauses for 4 s after the reader scrolls).
  useEffect(() => {
    let last: HTMLElement | null = null
    let lastScroll = 0
    const el = box.current
    const userScroll = () => (lastScroll = Date.now())
    el?.addEventListener('wheel', userScroll, { passive: true })
    el?.addEventListener('touchmove', userScroll, { passive: true })
    const off = playhead.subscribe((p) => {
      last?.classList.remove('cur')
      last = null
      if (!p.takeId || !words[p.takeId] || !box.current) return
      const i = wordAt(words[p.takeId], p.time)
      const node = box.current.querySelector(`[data-k="${p.takeId}:${i}"]`) as HTMLElement | null
      if (!node) return
      node.classList.add('cur')
      last = node
      if (p.playing && Date.now() - lastScroll > 4000) {
        const r = node.getBoundingClientRect()
        const b = box.current.getBoundingClientRect()
        if (r.top < b.top + 40 || r.bottom > b.bottom - 60) node.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
    })
    return () => {
      off()
      el?.removeEventListener('wheel', userScroll)
      el?.removeEventListener('touchmove', userScroll)
    }
  }, [playhead, words])

  useLayoutEffect(() => {
    if (!flash || !box.current || !words[flash.take]) return
    const i = Math.max(0, wordAt(words[flash.take], flash.t))
    const node = box.current.querySelector(`[data-k="${flash.take}:${i}"]`) as HTMLElement | null
    if (node) {
      node.scrollIntoView({ block: 'center' })
      node.classList.remove('flash')
      void node.offsetWidth
      node.classList.add('flash')
    }
  }, [flash, words])

  // Touch keeps the phone's own selection handles.
  const onTouchSelect = () => {
    const sel = window.getSelection()
    if (!sel || sel.isCollapsed || !box.current) return
    const a = sel.anchorNode?.parentElement?.closest('[data-k]') as HTMLElement | null
    const b = sel.focusNode?.parentElement?.closest('[data-k]') as HTMLElement | null
    if (!a || !b || !box.current.contains(a) || !box.current.contains(b)) return
    const [ta, ia] = a.dataset.k!.split(':')
    const [tb, ib] = b.dataset.k!.split(':')
    const take = iv.takes.find((t) => t.id === ta)!
    const w = words[ta]
    let x = Number(ia)
    let y = tb === ta ? Number(ib) : w.length - 1
    if (x > y) [x, y] = [y, x]
    if (y > x && sel.focusOffset === 0 && b === sel.focusNode?.parentElement) y--
    setPop({ kind: 'new', take, start: w[x].s, end: w[y].e, text: w.slice(x, y + 1).map((q) => q.w).filter((q) => !DASH.test(q)).join(' '), rect: sel.getRangeAt(0).getBoundingClientRect() })
  }

  // Mouse selection is ours, like a read-only doc: a click cues the word, a drag selects
  // whole words, and near the edges the page scrolls at a steady reading pace (the
  // browser's own drag-scroll races off).
  const drag = useRef<{ take: string; a: number; f: number; nodes: HTMLElement[]; x0: number; y0: number; x: number; y: number; moved: boolean; raf: number } | null>(null)
  const painted = useRef<HTMLElement[]>([])
  const clearSel = () => {
    for (const n of painted.current) n.classList.remove('sel')
    painted.current = []
  }
  const paint = (d: NonNullable<typeof drag.current>) => {
    clearSel()
    const [x, y] = d.a <= d.f ? [d.a, d.f] : [d.f, d.a]
    for (let i = x; i <= y; i++) {
      const n = d.nodes[i]
      if (n) {
        n.classList.add('sel')
        painted.current.push(n)
      }
    }
  }
  const wordAtPoint = (d: NonNullable<typeof drag.current>) => {
    const b = box.current!.getBoundingClientRect()
    const x = Math.min(Math.max(d.x, b.left + 2), b.right - 2)
    const y = Math.min(Math.max(d.y, b.top + 2), b.bottom - 2)
    const el = (document.elementFromPoint(x, y) as HTMLElement | null)?.closest('[data-k]') as HTMLElement | null
    if (!el || !box.current!.contains(el)) return d.f
    const [t, i] = el.dataset.k!.split(':')
    if (t === d.take) return Number(i)
    const order = iv.takes.map((q) => q.id)
    return order.indexOf(t) > order.indexOf(d.take) ? d.nodes.length - 1 : 0
  }
  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0 || !box.current) return
    const target = e.target as HTMLElement
    if (target.closest('.pickpop, button, input, textarea, select, a')) return
    const el = target.closest('[data-k]') as HTMLElement | null
    e.preventDefault()
    window.getSelection()?.removeAllRanges()
    if (!el) {
      setPop(null)
      clearSel()
      return
    }
    const [take, i] = el.dataset.k!.split(':')
    const nodes: HTMLElement[] = []
    box.current.querySelectorAll<HTMLElement>(`[data-k^="${take}:"]`).forEach((n) => (nodes[Number(n.dataset.k!.split(':')[1])] = n))
    const d = { take, a: Number(i), f: Number(i), nodes, x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, moved: false, raf: 0 }
    drag.current = d
    const move = (ev: MouseEvent) => {
      d.x = ev.clientX
      d.y = ev.clientY
      if (!d.moved && Math.hypot(d.x - d.x0, d.y - d.y0) > 4) {
        d.moved = true
        setPop(null)
      }
      if (d.moved) {
        d.f = wordAtPoint(d)
        paint(d)
      }
    }
    const tick = () => {
      const b = box.current?.getBoundingClientRect()
      if (b && d.moved) {
        const zone = 56
        const over = d.y < b.top + zone ? d.y - (b.top + zone) : d.y > b.bottom - zone ? d.y - (b.bottom - zone) : 0
        if (over) {
          // 1 to 9 px a frame, ramping across the edge zone and a little past it.
          const v = Math.sign(over) * Math.min(9, 1 + (Math.abs(over) / zone) * 5)
          box.current!.scrollTop += v
          d.f = wordAtPoint(d)
          paint(d)
        }
      }
      d.raf = requestAnimationFrame(tick)
    }
    d.raf = requestAnimationFrame(tick)
    const up = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      cancelAnimationFrame(d.raf)
      drag.current = null
      const tk = iv.takes.find((t) => t.id === d.take)!
      if (!d.moved) {
        clearSel()
        clickWord(tk, d.a, d.nodes[d.a].getBoundingClientRect())
        return
      }
      const w = words[d.take]
      const [x, y] = d.a <= d.f ? [d.a, d.f] : [d.f, d.a]
      const r1 = d.nodes[x].getBoundingClientRect()
      const r2 = d.nodes[y].getBoundingClientRect()
      const rect = new DOMRect(Math.min(r1.left, r2.left), r1.top, Math.max(r1.right, r2.right) - Math.min(r1.left, r2.left), r2.bottom - r1.top)
      setPop({ kind: 'new', take: tk, start: w[x].s, end: w[y].e, text: w.slice(x, y + 1).map((q) => q.w).filter((q) => !DASH.test(q)).join(' '), rect })
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }
  useEffect(() => () => {
    if (drag.current) cancelAnimationFrame(drag.current.raf)
  }, [])

  const clickWord = (take: Take, i: number, rect: DOMRect) => {
    const w = words[take.id][i]
    // The newest pick wins, the same one the highlight shows.
    const hit = picks.filter((p) => p.take_id === take.id && w.s >= p.start - 0.06 && w.e <= p.end + 0.06).sort((x, y) => y.created_at - x.created_at)[0]
    cam.current?.cue(take.id, clampToRolling(take, w.s))
    if (hit) setPop({ kind: 'edit', pick: hit, rect })
    else setPop(null)
  }

  const takeIds = iv.takes.map((t) => t.id).join(',')
  const renameSpeaker = useCallback((raw: string, name: string) => actions.renameSpeaker(iv.takes.map((t) => t.id), raw, name), [iv, actions])
  // Fix a wrong auto-grouping: move a take into another interview, or out on its own.
  return (
    <div className="transcript">
      <div className="tx-head">
        <div className="grow">
          <InterviewPicker ivs={allIvs} iv={iv} picks={picks} isTeam={isTeam} onPick={onPickIv} actions={actions} />
          <div className="muted small tx-meta">
            {clock(iv.duration)} · {iv.takes.length} take{iv.takes.length > 1 ? 's' : ''} · select any line to mark it
            {isTeam && iv.duration > 45 * 60 && <> · <button className="linkish" onClick={() => askSplit(iv.key)} title="One long recording with several people? Make one interview per person.">several people in here? Split it</button></>}
          </div>
        </div>
        <a className="btn btn-sm" href={`/api/p/${projectId}/export/txt?take=${takeIds}`}title="Download the transcript"><DownloadIcon size={14} /> <span className="btn-label">Transcript</span></a>
      </div>
      <div className="tx-body" ref={box} onMouseDown={onMouseDown} onTouchEnd={() => setTimeout(onTouchSelect, 30)}>
        {iv.takes.map((take, k) => (
          <TakeBlock key={take.id} take={take} n={k + 1} many={iv.takes.length > 1} tr={transcripts[take.id]} picks={picks.filter((p) => p.take_id === take.id)} words={words[take.id]} onTc={(t) => cam.current?.cue(take.id, clampToRolling(take, t))} onRenameSpeaker={isTeam ? renameSpeaker : undefined} />
        ))}
        {pop && (
          <PickPopover
            pop={pop}
            container={box.current}
            chapters={chapters}
            comments={comments}
            viewer={viewer}
            actions={actions}
            onPlay={(takeId, s, e) => (cam.current?.current().playing ? cam.current.pause() : cam.current?.playRange(takeId, s, e))}
            onClose={() => {
              setPop(null)
              clearSel()
              window.getSelection()?.removeAllRanges()
            }}
          />
        )}
      </div>
    </div>
  )
}

const TakeBlock = memo(function TakeBlock({ take, n, many, tr, picks, words, onTc, onRenameSpeaker }: {
  take: Take; n: number; many: boolean; tr?: Transcript; picks: Select[]; words: FlatWord[]
  onTc: (t: number) => void; onRenameSpeaker?: (raw: string, name: string) => void
}) {
  const [naming, setNaming] = useState<{ raw: string; pi: number; v: string } | null>(null)
  const saveName = () => {
    if (naming && naming.v.trim() && naming.v.trim() !== speakerName(take, naming.raw)) onRenameSpeaker?.(naming.raw, naming.v.trim())
    setNaming(null)
  }
  const hl = useMemo(() => {
    const m = new Map<number, Select>()
    for (const p of [...picks].sort((a, b) => a.created_at - b.created_at)) for (const w of words) if (w.s >= p.start - 0.06 && w.e <= p.end + 0.06) m.set(w.i, p)
    return m
  }, [picks, words])
  const paras = useMemo(() => (tr ? paragraphs(tr.segments) : []), [tr])
  const base: number[] = []
  let c = 0
  for (const s of tr?.segments || []) {
    base.push(c)
    c += s.words.length
  }
  return (
    <section className="take-block">
      {many && (
        <div className="take-divider">
          <span>Take {n}</span>
          <span className="muted">{take.tc_start != null ? takeTc(take, 0) : ''} · {clock(take.duration)}</span>
        </div>
      )}
      {!tr ? (
        <div className="tx-empty small">{take.status === 'failed' ? 'transcription failed' : take.status === 'none' ? 'no dialogue in this clip' : <><Spinner small /> transcribing…</>}</div>
      ) : (
        paras.map((p, pi) => {
          const name = speakerName(take, p.speaker)
          const same = pi > 0 && paras[pi - 1].speaker === p.speaker
          return (
            <div className={cls('para', !(name && !same) && 'nospk')} key={pi}>
              <div className="para-gutter"><button className="tc" onClick={() => onTc(p.start)} title="Cue here">{takeTc(take, p.start)}</button></div>
              <div className="para-main">
                {name && !same && (naming && naming.pi === pi ? (
                  <input className="speaker-input" autoFocus value={naming.v} onChange={(e) => setNaming({ ...naming, v: e.target.value })} onBlur={saveName} onKeyDown={(e) => { if (e.key === 'Enter') saveName(); else if (e.key === 'Escape') setNaming(null) }} onFocus={(e) => e.target.select()} aria-label="Speaker name" maxLength={60} />
                ) : onRenameSpeaker && name !== '?' ? (
                  <button className={cls('speaker', 'speaker-btn', name === 'Interviewer' && 'offcam')} onClick={() => setNaming({ raw: p.speaker!, pi, v: name })} title="Rename this speaker everywhere in the interview">{name}</button>
                ) : (
                  <div className={cls('speaker', name === '?' && 'unsure', name === 'Interviewer' && 'offcam')}>{name === '?' ? 'Speaker unclear' : name}</div>
                ))}
                <p>
                  {p.segs.map((si) =>
                    tr.segments[si].words.map((w, wi) => {
                      const i = base[si] + wi
                      if (DASH.test(w[0])) return null // older Whisper imports carry dialogue dashes as words
                      const s = hl.get(i)
                      return (
                        <span key={i} data-k={`${take.id}:${i}`} className={cls('w', s && 'hl hl-orange', s && hl.get(i - 1)?.id !== s.id && 'hl-start', s && hl.get(i + 1)?.id !== s.id && 'hl-end', s?.note && 'hl-note')}>
                          {w[0]}{' '}
                        </span>
                      )
                    }),
                  )}
                </p>
              </div>
            </div>
          )
        })
      )}
    </section>
  )
})

// The mark card: small, floats just above the selection so the line stays readable.
// Favorite (Enter) or one click on a chapter (or its number key). A comment is one
// optional line, opened from the icon.
function PickPopover({ pop, container, chapters, comments, viewer, actions, onPlay, onClose }: {
  pop: Pop; container: HTMLDivElement | null; chapters: Beat[]; comments: Comment[]; viewer: Viewer; actions: Actions
  onPlay: (takeId: string, s: number, e: number) => void; onClose: () => void
}) {
  const isNew = pop.kind === 'new'
  const pick = pop.kind === 'edit' ? pop.pick : null
  const mine = !pick || pick.author === viewer.name
  const canRemove = !!pick && (mine || viewer.kind === 'team')
  const [note, setNote] = useState(pick?.note || '')
  const [noting, setNoting] = useState(false)
  const [tags, setTags] = useState<string[]>(pick?.chapters || [])
  const [adding, setAdding] = useState(false)
  const [newChapter, setNewChapter] = useState('')
  const [busy, setBusy] = useState(false)
  const [reply, setReply] = useState('')
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const card = useRef<HTMLDivElement>(null)
  const thread = pick ? comments.filter((c) => c.select_id === pick.id) : []
  const ordered = useMemo(() => [...chapters].sort((x, y) => x.sort - y.sort), [chapters])

  const save = async (chs: string[]) => {
    if (busy || pop.kind !== 'new') return
    setBusy(true)
    const r = await actions.addSelect({ take_id: pop.take.id, start: pop.start, end: pop.end, text: pop.text, note: note.trim(), chapters: chs })
    setBusy(false)
    if (r) {
      const c = chs.length ? chapters.find((x) => x.id === chs[0]) : null
      toast(c ? `Added to ${c.title}` : 'Added to favorites')
      onClose()
    }
  }
  const toggle = async (id: string) => {
    const next = tags.includes(id) ? tags.filter((x) => x !== id) : [...tags, id]
    setTags(next)
    if (pick) await actions.updateSelect(pick.id, { chapters: next })
  }
  const choose = (id: string) => (isNew ? save([id]) : toggle(id))
  const addChapter = async () => {
    const t = newChapter.trim()
    if (!t) return setAdding(false)
    const b = await actions.addBeat(t)
    setNewChapter('')
    setAdding(false)
    if (b) await choose(b.id)
  }
  const saveNote = () => {
    if (pick && note.trim() !== pick.note) actions.updateSelect(pick.id, { note: note.trim() })
  }

  // Keys: Enter favorites, 1-9 files into that chapter, Esc closes. Typing in a field wins.
  const range = isNew ? { take: pop.take.id, s: pop.start, e: pop.end } : { take: pick!.take_id, s: pick!.start, e: pick!.end }
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onClose()
      if ((e.target as HTMLElement)?.closest('input, textarea')) return
      if (e.key === ' ' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        e.stopImmediatePropagation()
        return onPlay(range.take, range.s, range.e)
      }
      if (e.key === 'Enter' && isNew) {
        e.preventDefault()
        save([])
      } else if (/^[1-9]$/.test(e.key) && ordered[Number(e.key) - 1] && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        choose(ordered[Number(e.key) - 1].id)
      }
    }
    window.addEventListener('keydown', k, true)
    return () => window.removeEventListener('keydown', k, true)
  })
  // A click anywhere outside closes it (the transcript handles its own clicks).
  useEffect(() => {
    const down = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (!card.current?.contains(t) && !container?.contains(t)) onClose()
    }
    document.addEventListener('mousedown', down)
    return () => document.removeEventListener('mousedown', down)
  }, [container, onClose])
  // Above the selection when it fits in view, otherwise below; kept inside the column.
  useLayoutEffect(() => {
    if (!container || !card.current) return
    const cb = container.getBoundingClientRect()
    const h = card.current.offsetHeight
    const w = card.current.offsetWidth
    const above = pop.rect.top - cb.top - h - 10
    const top = (above >= 8 ? above : pop.rect.bottom - cb.top + 10) + container.scrollTop
    const left = Math.min(Math.max(8, pop.rect.left - cb.left - 6), Math.max(8, cb.width - w - 12))
    setPos((p) => (p && p.top === top && p.left === left ? p : { top, left }))
  })
  if (!container) return null

  return (
    <div ref={card} className="pickpop" style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }} role="dialog" aria-label={isNew ? 'Mark this line' : 'Marked line'} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
      <div className="pp-bar">
        {isNew ? (
          <button className="btn btn-primary pp-fav" onClick={() => save([])} disabled={busy}><HeartIcon /> Favorite <kbd>↵</kbd></button>
        ) : (
          <span className="pp-by"><HeartIcon /> <b>{pick!.author}</b>{pick!.author_kind === 'team' ? <span className="muted"> Osmo</span> : null}</span>
        )}
        {!isNew && <div className="grow" />}
        <button className="pp-ico play" onClick={() => onPlay(range.take, range.s, range.e)} aria-label="Play this line" title="Play it (space)"><PlayIcon /></button>
        {mine && <button className={cls('pp-ico', (noting || note) && 'on')} onClick={() => setNoting(true)} aria-label="Add a comment" title="Comment"><CommentIcon /></button>}
        {canRemove && <button className="pp-ico" onClick={() => { actions.deleteSelect(pick!.id); onClose() }} aria-label="Remove" title="Remove"><TrashIcon /></button>}
      </div>

      {mine && (noting || note) ? (
        <input
          className="pp-comment"
          autoFocus={noting}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={saveNote}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (isNew) save([])
              else (e.target as HTMLInputElement).blur()
            }
          }}
          placeholder={isNew ? 'Comment, then ↵' : 'Comment'}
          maxLength={2000}
        />
      ) : pick?.note ? (
        <p className="pp-note">{pick.note}</p>
      ) : null}

      {pick && (thread.length > 0 || !mine) && (
        <div className="pp-thread">
          {thread.map((c) => <div key={c.id} className="comment"><b>{c.author}</b> {c.body}</div>)}
          {!mine && (
            <form onSubmit={(e) => { e.preventDefault(); if (reply.trim()) { actions.addComment(pick.id, reply.trim()); setReply('') } }}>
              <input className="pp-comment" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply" maxLength={2000} />
            </form>
          )}
        </div>
      )}

      <div className="pp-label">{isNew ? 'or add to a chapter' : 'in chapters'}</div>
      <ol className="pop-chapters">
        {ordered.map((c, i) => (
          <li key={c.id}>
            <button className={cls('pop-chapter', tags.includes(c.id) && 'on')} onClick={() => choose(c.id)} title={c.title}>
              <span className="chapter-n">{i + 1}</span>
              <span className="grow ellipsis">{c.title}</span>
              {tags.includes(c.id) && <CheckIcon />}
            </button>
          </li>
        ))}
      </ol>
      <div className="pp-newrow">
          {adding ? (
            <form onSubmit={(e) => { e.preventDefault(); addChapter() }}>
              <input className="pp-comment" autoFocus value={newChapter} onChange={(e) => setNewChapter(e.target.value)} onBlur={() => !newChapter.trim() && setAdding(false)} placeholder="Chapter name, then ↵" maxLength={120} />
            </form>
          ) : (
            <button className="pop-chapter pp-new" onClick={() => setAdding(true)}><span className="chapter-n"><PlusIcon size={12} /></span><span className="grow">New chapter</span></button>
          )}
      </div>
    </div>
  )
}

// ── favorites (left) and chapters (right) ─────────────────────────────────
type PanelProps = { takes: Map<string, Take>; transcripts: Record<string, Transcript>; all: Interview[]; onJump: (takeId: string, t: number) => void; actions: Actions; viewer: Viewer; onPlayLine: (p: Select) => void }

function PickCard({ p, takes, transcripts, all, onJump, onDropBefore, actions, viewer, onPlayLine, chapterId }: { p: Select; onDropBefore?: (id: string) => void; chapterId?: string } & PanelProps) {
  const take = takes.get(p.take_id)
  const [over, setOver] = useState(false)
  if (!take) return null
  const sp = speakerName(take, speakerAt(transcripts[p.take_id], p.start, p.end))
  const iv = all.find((g) => g.takes.some((t) => t.id === p.take_id))
  const canRemove = p.author === viewer.name || viewer.kind === 'team'
  const takeOut = async () => {
    const before = { chapters: p.chapters, beat_sort: p.beat_sort }
    await actions.updateSelect(p.id, { chapters: p.chapters.filter((c) => c !== chapterId) })
    undoToast('Taken out of the chapter', () => actions.updateSelect(p.id, before))
  }
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation()
    fn()
  }
  return (
    <div
      className={cls('pick', over && 'drop-before')}
      role="button"
      tabIndex={0}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData('text/osel-pick', p.id); e.dataTransfer.effectAllowed = 'move' }}
      {...(onDropBefore && {
        onDragOver: (e: React.DragEvent) => {
          if (!e.dataTransfer.types.includes('text/osel-pick')) return
          e.preventDefault()
          e.stopPropagation()
          setOver(true)
        },
        onDragLeave: () => setOver(false),
        onDrop: (e: React.DragEvent) => {
          e.preventDefault()
          e.stopPropagation()
          setOver(false)
          const id = e.dataTransfer.getData('text/osel-pick')
          if (id && id !== p.id) onDropBefore(id)
        },
      })}
      onClick={() => onJump(p.take_id, p.start)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter') onJump(p.take_id, p.start)
        else if ((e.key === 'Backspace' || e.key === 'Delete') && (chapterId || canRemove)) {
          e.preventDefault()
          if (chapterId) takeOut()
          else actions.deleteSelect(p.id)
        }
      }}
    >
      <div className="pick-quote">{p.text}</div>
      {p.note && <div className="pick-note"><CommentIcon size={12} /> {p.note}</div>}
      <div className="pick-meta">
        <span className="pick-who">{sp && sp !== '?' ? sp : iv?.name}</span>
        <span>{lineLen(p.end - p.start)}</span>
        <span className="pick-tc">{takeTc(take, p.start)}</span>
        <span className="grow" />
        <span className={cls('pick-by', p.author === viewer.name && 'me')} title={`Marked by ${p.author === viewer.name ? 'you' : p.author}`}>{initials(p.author)}</span>
      </div>
      <span className="pick-tools">
        <button className="icon-btn play" onClick={stop(() => onPlayLine(p))} aria-label="Play this line" title="Play"><PlayIcon /></button>
        {chapterId && <button className="icon-btn" onClick={stop(takeOut)} aria-label="Take out of chapter" title="Take out of this chapter (stays a favorite)"><CloseIcon /></button>}
        {canRemove && <button className="icon-btn" onClick={stop(() => actions.deleteSelect(p.id))} aria-label="Remove line" title="Remove"><TrashIcon /></button>}
      </span>
    </div>
  )
}

const pickOrder = (all: Interview[]) => (p: Select) => {
  const g = all.findIndex((x) => x.takes.some((t) => t.id === p.take_id))
  const k = all[g]?.takes.findIndex((t) => t.id === p.take_id) ?? 0
  return g * 1e7 + k * 1e5 + p.start
}

function useDrop(onPick: (id: string) => void) {
  const [over, setOver] = useState(false)
  return {
    over,
    props: {
      onDragOver: (e: React.DragEvent) => {
        if (e.dataTransfer.types.includes('text/osel-pick')) {
          e.preventDefault()
          setOver(true)
        }
      },
      onDragLeave: () => setOver(false),
      onDrop: (e: React.DragEvent) => {
        e.preventDefault()
        setOver(false)
        const id = e.dataTransfer.getData('text/osel-pick')
        if (id) onPick(id)
      },
    },
  }
}

function FavoritesList({ picks, who, setWho, people, actions, ...rest }: { picks: Select[]; who: 'all' | 'mine'; setWho: (w: 'all' | 'mine') => void; people: number } & PanelProps) {
  const drop = useDrop((id) => actions.updateSelect(id, { chapters: [] }))
  const order = pickOrder(rest.all)
  return (
    <section className={cls('favs', drop.over && 'over')} {...drop.props}>
      <div className="favs-head">
        <h3><HeartIcon className="i-heart" /> Favorites <span className="muted">{picks.length}</span></h3>
        <div className="seg">
          <button className={cls(who === 'all' && 'on')} onClick={() => setWho('all')}>Everyone</button>
          <button className={cls(who === 'mine' && 'on')} onClick={() => setWho('mine')}>Mine</button>
        </div>
      </div>
      {picks.length === 0 ? (
        <p className="muted small favs-empty">Select any line in the transcript and hit Favorite, or drop it straight into a chapter.</p>
      ) : (
        <div className="picks-list">{[...picks].sort((x, y) => order(x) - order(y)).map((p) => <PickCard key={p.id} p={p} {...rest} actions={actions} />)}</div>
      )}
    </section>
  )
}

function ChaptersPanel({ picks, chapters, actions, onPlayList, ...rest }: { picks: Select[]; chapters: Beat[]; onPlayList: (list: Select[]) => void } & PanelProps) {
  const [adding, setAdding] = useState('')
  const ordered = [...chapters].sort((a, b) => a.sort - b.sort)
  const order = pickOrder(rest.all)
  const move = async (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= ordered.length) return
    const next = [...ordered]
    ;[next[i], next[j]] = [next[j], next[i]]
    // Renumber so equal or gappy sorts can't stall a move.
    for (let k = 0; k < next.length; k++) if (next[k].sort !== k + 1) await actions.updateBeat(next[k].id, { sort: k + 1 })
  }
  const inOrder = (c: Beat) => picks.filter((p) => p.chapters.includes(c.id)).sort((x, y) => x.beat_sort - y.beat_sort || order(x) - order(y))
  const story = ordered.flatMap(inOrder)
  return (
    <div className="chapters">
      <div className="chapters-head">
        <h3>Chapters</h3>
        <span className="muted small">{plural(story.length, 'line')}</span>
        {story.length > 0 && <button className="btn btn-sm play-story" onClick={() => onPlayList(story)} title="Play every chapter in order"><PlayIcon size={14} /> Play</button>}
      </div>
      <p className="muted small chapters-hint">The story, in order. Select a line and pick a chapter, or drag favorites in.</p>
      <ol className="chapter-list">
        {ordered.map((c, i) => (
          <ChapterSection key={c.id} n={i + 1} chapter={c} picks={inOrder(c)} onPlay={onPlayList} first={i === 0} last={i === ordered.length - 1} onMove={(d) => move(i, d)} actions={actions} {...rest} />
        ))}
      </ol>
      <form className="chapter-add" onSubmit={async (e) => { e.preventDefault(); if (adding.trim()) { await actions.addBeat(adding.trim()); setAdding('') } }}>
        <input value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="+ Add a chapter" maxLength={120} />
      </form>
    </div>
  )
}

function ChapterSection({ n, chapter, picks, first, last, onMove, onPlay, actions, ...rest }: { n: number; chapter: Beat; picks: Select[]; first: boolean; last: boolean; onMove: (d: -1 | 1) => void; onPlay: (list: Select[]) => void } & PanelProps) {
  const [v, setV] = useState(chapter.title)
  useEffect(() => setV(chapter.title), [chapter.title])
  // Dropped on the chapter: goes to the end. Dropped on a line: lands just before it.
  const tail = picks.length ? picks[picks.length - 1].beat_sort : 0
  const drop = useDrop((id) => actions.updateSelect(id, { chapters: [chapter.id], beat_sort: Math.max(Date.now(), tail + 1) }))
  const before = (k: number) => (id: string) => {
    const at = picks[k].beat_sort
    const prev = k > 0 ? picks[k - 1].beat_sort : at - 2000
    actions.updateSelect(id, { chapters: [chapter.id], beat_sort: (prev + at) / 2 })
  }
  return (
    <li className={cls('chapter', drop.over && 'over')} {...drop.props}>
      <div className="chapter-top">
        <span className="chapter-n">{n}</span>
        <input className="chapter-name" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v.trim() && v.trim() !== chapter.title && actions.updateBeat(chapter.id, { title: v.trim() })} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} maxLength={120} aria-label="Chapter name" />
        <span className="chapter-tools">
          {picks.length > 0 && <button className="icon-btn play" onClick={() => onPlay(picks)} aria-label="Play this chapter" title="Play this chapter"><PlayIcon /></button>}
        <button className="icon-btn" disabled={first} onClick={() => onMove(-1)} aria-label="Move up" title="Move up"><ArrowUpIcon /></button>
        <button className="icon-btn" disabled={last} onClick={() => onMove(1)} aria-label="Move down" title="Move down"><ArrowDownIcon /></button>
        <button className="icon-btn" onClick={() => actions.deleteBeat(chapter.id)} aria-label="Remove chapter" title="Remove chapter (its picks go back to favorites)"><CloseIcon /></button>
        </span>
      </div>
      {picks.length ? <div className="picks-list">{picks.map((p, k) => <PickCard key={p.id} p={p} {...rest} actions={actions} chapterId={chapter.id} onDropBefore={before(k)} />)}</div> : <div className="chapter-empty">No lines yet</div>}
    </li>
  )
}

// ── share (Drive style: add people by email, general access, copy link) ───
type Invite = { id: string; email: string; name: string; invited_by: string; created_at: number; last_used_at: number | null }

/*
  First cut: the team hands everyone's picks to the Osmo agent, which cuts a V1 from them. The cut
  and its review live on post.osmo.inc; this only starts it and says where to watch.
*/
const REVIEWS = location.hostname.endsWith('osmo.inc') ? 'https://post.osmo.inc' : ''
function FirstCutDialog({ projectId, title, picks, onClose }: { projectId: string; title: string; picks: Select[]; onClose: () => void }) {
  const [brief, setBrief] = useState('')
  const [length, setLength] = useState('')
  const [to, setTo] = useState('')
  const [busy, setBusy] = useState(false)
  const [started, setStarted] = useState<{ id: string; version: number } | null>(null)
  const client = picks.filter((p) => p.author_kind === 'client').length
  const start = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      const emails = to.split(/[\s,;]+/).filter(Boolean)
      const r = await api.post<{ edit: { id: string; version: number } }>(`p/${projectId}/edits`, { brief: brief.trim() || undefined, options: { from_picks: true, ...(Number(length) > 0 ? { length_sec: Number(length) } : {}), ...(emails.length ? { review: { to: emails } } : {}) } })
      setStarted(r.edit)
    } catch (x) {
      toast(x instanceof ApiError ? x.message : 'Could not start the cut', 'err')
    } finally {
      setBusy(false)
    }
  }
  if (started)
    return (
      <Modal title="First cut started" onClose={onClose}>
        <p>The Osmo agent is cutting request {started.version} from {plural(picks.length, 'pick', 'picks')}. It takes about ten minutes. You'll get an email with the review link when it's ready.</p>
        <div className="sh-foot">
          <a className="btn" href={`${REVIEWS}/projects/${projectId}#live`} target="_blank" rel="noreferrer">Watch it work</a>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </Modal>
    )
  return (
    <Modal title="First cut from the picks" onClose={onClose}>
      <p className="muted small sh-what">{title}: {plural(picks.length, 'pick', 'picks')}{client ? `, ${client} from the client` : ''}. The Osmo agent builds a V1 from them, in their chapters, and it opens on post.osmo.inc to watch and comment on.</p>
      <form className="sh-add" onSubmit={start}>
        <textarea value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Anything to add? Who it's for, the tone, what to open on (optional)" rows={3} maxLength={4000} autoFocus />
        <select className="sh-select" value={length} onChange={(e) => setLength(e.target.value)} aria-label="Length">
          <option value="">Length: whatever the picks need</option>
          <option value="30">About 30 seconds</option>
          <option value="60">About 1 minute</option>
          <option value="120">About 2 minutes</option>
          <option value="180">About 3 minutes</option>
          <option value="300">About 5 minutes</option>
        </select>
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Also send it for review to (emails, optional)" aria-label="Send for review to" type="text" inputMode="email" autoComplete="off" />
        <div className="sh-send"><button className="btn btn-primary" disabled={busy}>{busy ? 'Starting…' : 'Make the first cut'}</button></div>
      </form>
    </Modal>
  )
}

function ShareDialog({ projectId, title, onClose }: { projectId: string; title: string; onClose: () => void }) {
  const [info, setInfo] = useState<{ access: 'invited' | 'link'; url: string; invites: Invite[]; stringout?: boolean } | null>(null)
  const [emails, setEmails] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const load = () => api.get<typeof info>(`projects/${projectId}/access`).then(setInfo).catch(() => toast('Could not load sharing', 'err'))
  useEffect(() => {
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const invite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!emails.trim()) return
    setBusy(true)
    try {
      const r = await api.post<{ sent: number; team: string[] }>(`projects/${projectId}/invites`, { emails, message })
      const n = emails.split(/[\s,;]+/).filter(Boolean).length - r.team.length
      toast(r.team.length && !n ? 'Osmo people already have access' : `Invited ${plural(n, 'person', 'people')}`)
      setEmails('')
      setMessage('')
      load()
    } catch (x) {
      toast(x instanceof ApiError ? x.message : 'Could not invite', 'err')
    } finally {
      setBusy(false)
    }
  }
  const setAccess = async (access: 'invited' | 'link') => {
    setInfo((i) => i && { ...i, access })
    try {
      await api.put(`projects/${projectId}/access`, { access })
    } catch {
      toast('Could not change access', 'err')
      load()
    }
  }
  const setStringout = async (on: boolean) => {
    setInfo((i) => i && { ...i, stringout: on })
    try {
      await api.put(`projects/${projectId}/stringout`, { on })
      window.dispatchEvent(new Event('osel:stringout'))
    } catch {
      toast('Could not change that', 'err')
      load()
    }
  }
  const remove = async (iv: Invite) => {
    setInfo((i) => i && { ...i, invites: i.invites.filter((x) => x.id !== iv.id) })
    await api.del(`projects/${projectId}/invites/${iv.id}`).catch(() => load())
  }
  const open = info?.access === 'link'
  return (
    <Modal title="Share" onClose={onClose}>
      <p className="muted small sh-what">{title}</p>
      <form className="sh-add" onSubmit={invite}>
        <input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="Add people by email" aria-label="Add people by email" type="text" inputMode="email" autoComplete="off" autoFocus />
        {emails.trim() && (
          <>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message (optional)" rows={2} maxLength={1000} />
            <div className="sh-send"><button className="btn btn-primary" disabled={busy}>{busy ? 'Sending…' : 'Send invite'}</button></div>
          </>
        )}
      </form>
      <div className="sh-label">People with access</div>
      <div className="sh-people">
        <div className="sh-person">
          <span className="sh-av team"><UsersIcon /></span>
          <div className="grow"><b>Everyone at Osmo</b><div className="muted small">Anyone with an osmo.inc account</div></div>
          <span className="muted small">Full access</span>
        </div>
        {info?.invites.map((iv) => (
          <div key={iv.id} className="sh-person">
            <span className="sh-av">{(iv.name || iv.email)[0].toUpperCase()}</span>
            <div className="grow ellipsis"><b>{iv.name || iv.email}</b><div className="muted small ellipsis">{iv.name ? `${iv.email} · ` : ''}{iv.last_used_at ? `opened ${new Date(iv.last_used_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : 'invited'}</div></div>
            <button className="icon-btn" aria-label={`Remove ${iv.email}`} title="Remove access" onClick={() => remove(iv)}><TrashIcon /></button>
          </div>
        ))}
      </div>
      <div className="sh-label">General access</div>
      <div className="sh-person">
        <span className={cls('sh-av', open && 'on')}>{open ? <GlobeIcon /> : <LockIcon />}</span>
        <div className="grow">
          <select className="sh-select" value={info?.access || 'invited'} onChange={(e) => setAccess(e.target.value as 'invited' | 'link')} aria-label="General access" disabled={!info}>
            <option value="invited">Only people invited</option>
            <option value="link">Anyone with the link</option>
          </select>
          <div className="muted small">{open ? 'Anyone with the link can watch, mark lines and comment' : 'Only the people above can open it'}</div>
        </div>
      </div>
      <p className="muted small sh-note">Clients can watch, read, favorite lines, comment and build chapters. They can't add footage, edit transcripts or see other projects.</p>
      <label className="toggle sh-stringout">
        <input type="checkbox" checked={!!info?.stringout} disabled={!info} onChange={(e) => setStringout(e.target.checked)} />
        <span>Clients can submit their favorites for a stringout. Posty cuts it and emails it to them.</span>
      </label>
      <div className="sh-foot">
        <button className="btn" onClick={() => { if (info) { copy(info.url); toast(open ? 'Link copied' : 'Link copied. Only invited people can open it') } }}><LinkIcon /> Copy link</button>
        <button className="btn btn-primary" onClick={onClose}>Done</button>
      </div>
    </Modal>
  )
}

// ── submit for stringout (Posty cuts the favorites in order and emails it) ──
type StringoutInfo = { enabled: boolean; favorites: number; email: string | null; stringout: { id: string; version: number; status: string; by: string; cutting: boolean } | null }

/*
  The client's button under the chapters. Shown to clients once the team turns it on (Share), and to
  the team always. While Posty cuts, it says so instead of offering a second one.
*/
function StringoutBar({ projectId, isTeam, picks }: { projectId: string; isTeam: boolean; picks: number }) {
  const [info, setInfo] = useState<StringoutInfo | null>(null)
  const [open, setOpen] = useState(false)
  const load = useCallback(() => api.get<StringoutInfo>(`p/${projectId}/submit-picks`).then(setInfo).catch(() => {}), [projectId])
  useEffect(() => {
    load()
  }, [load, picks])
  // The team flipping the switch in Share shows or hides it here at once.
  useEffect(() => {
    window.addEventListener('osel:stringout', load)
    return () => window.removeEventListener('osel:stringout', load)
  }, [load])
  const cutting = !!info?.stringout?.cutting
  useEffect(() => {
    if (!cutting) return
    const t = window.setInterval(load, 30_000)
    return () => window.clearInterval(t)
  }, [cutting, load])
  if (!info || (!info.enabled && !isTeam)) return null
  return (
    <div className="stringout-bar">
      {cutting ? (
        <p className="small stringout-status" role="status"><Spinner small /> <span>Posty is cutting a stringout{info.stringout?.by ? ` for ${info.stringout.by}` : ''}. We'll email it when it's ready.</span></p>
      ) : (
        <button className="btn btn-primary stringout-btn" disabled={!picks} onClick={() => setOpen(true)} title={picks ? 'Posty cuts your favorites into one stringout and emails it to you' : 'Favorite some lines first'}>
          Submit for stringout
        </button>
      )}
      {isTeam && !info.enabled && <p className="muted small stringout-off">Clients don't see this yet. Turn it on in Share.</p>}
      {open && <StringoutDialog projectId={projectId} info={info} picks={picks} onClose={() => setOpen(false)} onDone={load} />}
    </div>
  )
}

function StringoutDialog({ projectId, info, picks, onClose, onDone }: { projectId: string; info: StringoutInfo; picks: number; onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState(info.email || '')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [sentTo, setSentTo] = useState<string | null>(null)
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      const r = await api.post<{ email: string }>(`p/${projectId}/submit-picks`, { email: email.trim(), note: note.trim() || undefined })
      setSentTo(r.email)
      onDone()
    } catch (x) {
      // One is already cutting: show that instead of the form.
      if (x instanceof ApiError && x.status === 409) {
        onDone()
        onClose()
      }
      toast(x instanceof ApiError ? x.message : 'Could not submit. Try again.', 'err')
    } finally {
      setBusy(false)
    }
  }
  if (sentTo)
    return (
      <Modal title="Submitted" onClose={onClose}>
        <p className="stringout-done">Got it. Posty is cutting your stringout, we'll email you at <b>{sentTo}</b> when it's ready.</p>
        <div className="sh-foot"><span /><button className="btn btn-primary" onClick={onClose}>Done</button></div>
      </Modal>
    )
  return (
    <Modal title="Submit for stringout" onClose={onClose}>
      <p className="muted small sh-what">Posty, the Osmo editor, cuts your {plural(picks, 'favorite', 'favorites')} into one video: chapters in order, then the rest in the order you marked them, with your comments.</p>
      <form className="sh-add" onSubmit={submit}>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email, so we can send it to you" aria-label="Your email" type="email" inputMode="email" autoComplete="email" required autoFocus />
        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything Posty should know? (optional)" aria-label="Note" rows={3} maxLength={2000} />
        <div className="sh-send"><button className="btn btn-primary" disabled={busy || !picks}>{busy ? 'Submitting…' : `Submit ${plural(picks, 'favorite', 'favorites')}`}</button></div>
      </form>
    </Modal>
  )
}
